import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { clients as clientsApi, plans as plansApi } from '../api/client.js';
import { useAuth } from '../auth/AuthContext.jsx';
import ClientItems from './ClientItems.jsx';
import ClientHistory from './ClientHistory.jsx';
import ClientMap from './ClientMap.jsx';
import DeliverySummary from './DeliverySummary.jsx';
import {
  ArrowLeftIcon, UserIcon, PhoneIcon, MailIcon, PinIcon,
  PackageIcon, ClockIcon, NoteIcon, NavigateIcon, TruckIcon,
} from '../common/icons.jsx';

const STATUS_LABELS = {
  pending: 'En attente',
  in_progress: 'En cours',
  delivered: 'Livré',
  problem: 'Problème',
};

const STATUS_COLORS = {
  pending: 'bg-gray-100 text-gray-600 border border-gray-200',
  in_progress: 'bg-orange-50 text-orange-700 border border-orange-200',
  delivered: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  problem: 'bg-red-50 text-red-700 border border-red-200',
};

export default function ClientDetail() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { user, campaigns, activeCampaign, viewAsRole } = useAuth();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [delivering, setDelivering] = useState(false);

  const campaignEntry = campaigns.find((c) => c.campaignId === activeCampaign);
  const effectiveRole = viewAsRole || campaignEntry?.role;
  const canDeliver = effectiveRole === 'coordinator' || effectiveRole === 'admin' || user?.globalRole === 'superadmin';

  useEffect(() => {
    clientsApi.get(clientId)
      .then(setClient)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [clientId]);

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage text={error} />;
  if (!client) return <ErrorMessage text="Client introuvable" />;

  const navigateUrl = client.lat && client.lng
    ? `https://www.google.com/maps/dir/?api=1&destination=${client.lat},${client.lng}&travelmode=driving`
    : null;
  const wazeUrl = client.lat && client.lng
    ? `https://waze.com/ul?ll=${client.lat},${client.lng}&navigate=yes`
    : null;

  /** @param {number} lat @param {number} lng */
  const handleCoordsUpdate = (lat, lng) => {
    setClient((prev) => ({ ...prev, lat, lng }));
  };

  /** Create a quick ad-hoc plan for this single client and start delivery */
  const handleQuickDeliver = async () => {
    if (delivering) return;
    setDelivering(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const plan = await plansApi.create({
        date: today,
        driverId: user.id,
        clientIds: [clientId],
        startTime: new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
        vehicleCapacity: 42,
      });
      navigate(`/delivery/${plan.id}?stop=${plan.stops?.[0]?.id || ''}`);
    } catch (err) {
      setError(err.message);
      setDelivering(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Back link */}
      <Link to="/clients"
        className="inline-flex items-center gap-1.5 text-sm text-[#1e40af] hover:text-[#0f172a] transition-colors min-h-[44px]">
        <ArrowLeftIcon size={16} />
        <span>Retour clients</span>
      </Link>

      {/* Header with name + status badge */}
      <div className="bg-white rounded-2xl p-5 shadow-sm">
        <div className="flex justify-between items-start gap-3">
          <h2 className="text-2xl font-bold text-[#0f172a] leading-tight">{client.name}</h2>
          <span className={`text-xs font-semibold px-3 py-1.5 rounded-full whitespace-nowrap ${STATUS_COLORS[client.delivery_status]}`}>
            {STATUS_LABELS[client.delivery_status]}
          </span>
        </div>
        {client.contact_name && (
          <p className="text-sm text-[#64748b] mt-1">{client.contact_name}</p>
        )}
      </div>

      {/* Info cards */}
      <div className="grid grid-cols-1 gap-3">
        <InfoCard icon={<PinIcon className="text-[#0891b2]" />} title="Adresse">
          {client.address && <p className="text-sm text-[#0f172a]">{client.address}</p>}
          {client.city && (
            <p className="text-sm text-[#64748b]">{client.postal_code || ''} {client.city}</p>
          )}
        </InfoCard>

        {(client.phone || client.email) && (
          <InfoCard icon={<PhoneIcon className="text-[#1e40af]" />} title="Coordonnées">
            {client.phone && (
              <a href={`tel:${client.phone}`} className="flex items-center gap-2 text-sm text-[#0f172a] min-h-[44px]">
                <PhoneIcon size={14} className="text-[#64748b]" />
                {client.phone}
              </a>
            )}
            {client.email && (
              <a href={`mailto:${client.email}`} className="flex items-center gap-2 text-sm text-[#1e40af] hover:underline">
                <MailIcon size={14} className="text-[#64748b]" />
                {client.email}
              </a>
            )}
          </InfoCard>
        )}

        <InfoCard icon={<PackageIcon className="text-[#f97316]" />} title="Livraison">
          <div className="flex items-center gap-4 text-sm">
            <span className="font-semibold text-[#0f172a]">{client.nb_units || 0} unités</span>
            {client.time_slot && (
              <span className="flex items-center gap-1 text-[#64748b]">
                <ClockIcon size={14} /> {client.time_slot}
              </span>
            )}
          </div>
          {client.delivery_notes && (
            <div className="flex items-start gap-2 mt-2 text-sm text-[#64748b] bg-amber-50 rounded-lg p-2.5">
              <NoteIcon size={14} className="mt-0.5 flex-shrink-0 text-amber-600" />
              <span>{client.delivery_notes}</span>
            </div>
          )}
        </InfoCard>
      </div>

      {/* Mini map */}
      <ClientMap clientId={clientId} lat={client.lat} lng={client.lng} onCoordsUpdate={handleCoordsUpdate} />

      {/* Quick deliver button */}
      {canDeliver && client.delivery_status !== 'delivered' && (
        <button
          onClick={handleQuickDeliver}
          disabled={delivering}
          className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-[#059669] to-emerald-500 hover:opacity-90 text-white rounded-xl p-4 font-semibold text-sm transition-all min-h-[52px] shadow-lg shadow-emerald-500/20 disabled:opacity-50"
        >
          <TruckIcon size={18} />
          {delivering ? 'Création de la tournée...' : 'Livrer ce client (hors-tournée)'}
        </button>
      )}

      {/* Navigation buttons */}
      {(navigateUrl || wazeUrl) && (
        <div className="flex gap-3">
          {navigateUrl && (
            <a href={navigateUrl} target="_blank" rel="noopener noreferrer"
              className="flex-1 flex items-center justify-center gap-2 bg-[#1e40af] hover:bg-[#1e3a8a] text-white rounded-xl p-4 font-medium text-sm transition-colors min-h-[44px]">
              <NavigateIcon size={18} />
              Google Maps
            </a>
          )}
          {wazeUrl && (
            <a href={wazeUrl} target="_blank" rel="noopener noreferrer"
              className="flex-1 flex items-center justify-center gap-2 bg-[#0891b2] hover:bg-[#0e7490] text-white rounded-xl p-4 font-medium text-sm transition-colors min-h-[44px]">
              <NavigateIcon size={18} />
              Waze
            </a>
          )}
        </div>
      )}

      {/* Delivery summary (for delivered/processed clients) */}
      <DeliverySummary clientId={clientId} />

      {/* Units list */}
      <ClientItems clientId={clientId} />

      {/* History timeline */}
      <ClientHistory clientId={clientId} />
    </div>
  );
}

/**
 * Card wrapper for grouped info with icon and title.
 * @param {{ icon: JSX.Element, title: string, children: React.ReactNode }} props
 */
function InfoCard({ icon, title, children }) {
  return (
    <div className="bg-white rounded-2xl p-4 shadow-sm">
      <div className="flex items-center gap-2 mb-2.5">
        {icon}
        <h3 className="text-xs font-semibold text-[#64748b] uppercase tracking-wider">{title}</h3>
      </div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-12">
      <div className="animate-spin h-8 w-8 border-4 border-[#1e40af] border-t-transparent rounded-full" />
    </div>
  );
}

/** @param {{ text: string }} props */
function ErrorMessage({ text }) {
  return (
    <div className="flex items-center justify-center py-12">
      <p className="text-red-600 text-center bg-red-50 rounded-xl px-6 py-4">{text}</p>
    </div>
  );
}
